import React from "react";
import ProductPageLayout from "@/components/ProductPageLayout";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, Bell } from "lucide-react";

const STATUS_URL = "https://status.quantumworkplace.com/";

const Status = () => {
  return (
    <ProductPageLayout
      title="Uptime Status"
      description="Real-time status and uptime information for Assembly's products and services. Monitor our system health and get notified of any incidents."
      keywords="Assembly status, system status, uptime monitoring, service health, platform status"
      ctaTitle="Questions About Our Service?"
      ctaDescription="Contact our support team if you have questions about our system status or service availability."
      ctaButtonText="Contact Support"
      ctaVariant="blue"
      showCTA={false}
    >
      {/* Header */}
      <section className="pt-14 pb-8 sm:pt-20 sm:pb-10 lg:pt-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          <div className="flex flex-col gap-7 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                Service status
              </p>
              <h1 className="mt-3 text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
                System Status
              </h1>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
                Live uptime, incident history, and scheduled maintenance for
                Assembly and Quantum Workplace services.
              </p>
            </div>

            <Button
              asChild
              variant="outline"
              size="lg"
              className="shrink-0 rounded-lg border-border bg-card px-5"
            >
              <a
                href={STATUS_URL}
                target="_blank"
                rel="noopener noreferrer"
                title="Open the status page in a new tab"
              >
                Open status page
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Embedded status page */}
      <section className="pb-16 sm:pb-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
            <iframe
              src={STATUS_URL}
              title="Quantum Workplace Status Page"
              loading="lazy"
              className="h-[3200px] w-full border-0 md:h-[3550px] lg:h-[3700px]"
            />
          </div>
        </div>
      </section>

      {/* Subscribe */}
      <section className="pb-20 sm:pb-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          <div className="flex flex-col gap-6 rounded-2xl border border-border bg-card px-6 py-7 sm:flex-row sm:items-center sm:justify-between sm:px-8">
            <div className="flex items-start gap-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted text-primary">
                <Bell className="h-5 w-5" />
              </span>
              <div>
                <h2 className="text-base font-semibold text-foreground sm:text-lg">
                  Get notified about incidents and maintenance
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Subscribe on the status page to receive updates by email or
                  Slack.
                </p>
              </div>
            </div>

            <div className="flex shrink-0 flex-wrap gap-3">
              <Button asChild size="lg" className="rounded-lg px-6">
                <a
                  href={`${STATUS_URL}#updates-dropdown-email`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Subscribe to status updates by email"
                >
                  Email
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="rounded-lg border-border bg-transparent px-6"
              >
                <a
                  href={`${STATUS_URL}#updates-dropdown-slack`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Subscribe to status updates in Slack"
                >
                  Slack
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </ProductPageLayout>
  );
};

export default Status;
